<script lang="ts">
  import { UsersTexts } from '../../UsersTexts';
  import { onMount } from 'svelte';
  import * as Icons from '@lucide/svelte';
  import Box from '../../../../components/boxes/Box.svelte';
  import Content from '../../../../components/content/Content.svelte';
  import Form from '../../../../components/forms/Form.svelte';
  import FormInput from '../../../../components/forms/FormInput.svelte';
  import FormButton from '../../../../components/forms/FormButton.svelte';
  import FormButtonLine from '../../../../components/forms/FormButtonLine.svelte';
  import ErrorMessages from '../../../../components/boxes/ErrorMessages.svelte';
  import QrCode from '../../../../components/content/QrCode.svelte';
  import { ApiService } from '../../../../api/services/ApiService';
  import { UserService } from '../../../../services/UserService.svelte';
  import type { TfaAppSetupBody } from '../../../../api/types/users.types';

  let SetupData = $state<TfaAppSetupBody | null>(null);
  let Code = $state('');
  let IsLoading = $state(false);
  let Errors = $state<string[]>([]);

  onMount(() => {
    void loadSetup();
  });

  async function loadSetup(): Promise<void> {
    IsLoading = true;
    Errors = [];
    const Result = await ApiService.users.startTfaAppSetup();
    IsLoading = false;

    if (!Result.isOk || Result.body === undefined) {
      Errors = ApiService.listErrorTexts(Result);
      return;
    }

    SetupData = Result.body;
  }

  async function submitConfirm(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (IsLoading || !SetupData) {
      return;
    }

    IsLoading = true;
    Errors = [];
    const Result = await ApiService.users.confirmTfaApp(SetupData.secret, Code);
    IsLoading = false;

    if (!Result.isOk) {
      Errors = ApiService.listErrorTexts(Result);
      return;
    }

    await UserService.refresh();
  }
</script>

<Box layerAbs={true} lightDarker={true}>
  {#snippet headline()}{UsersTexts.TfaSetup}{/snippet}
  <Content>
    <p>{UsersTexts.TfaForcedIntro}</p>
  </Content>

  <ErrorMessages errors={Errors} />

  {#if SetupData === null}
    <Content>
      <p>{UsersTexts.TfaSetupLoading}</p>
    </Content>
  {:else}
    <Content>
      <p>{UsersTexts.TfaScan}</p>
      <div class="qrContainer">
        <div class="qrWrapper">
          <QrCode text={SetupData.uri} size="12rem" />
        </div>
      </div>
      <p class="secretLine">
        <strong>{UsersTexts.TfaKey}:</strong>
        <code class="secretCode">{SetupData.secret}</code>
      </p>
      <p style="margin-top: 1.5rem;">{UsersTexts.TfaConfirmCode}</p>
    </Content>
    <Form onsubmit={submitConfirm}>
      <FormInput autofocus type="code" label={UsersTexts.TfaCode} name="code" required bind:value={Code} />
      <FormButtonLine>
        <FormButton type="submit" state="link" icon={Icons.Link} disabled={IsLoading || !Code}>{UsersTexts.TfaLink}</FormButton>
      </FormButtonLine>
    </Form>
  {/if}
</Box>

<style lang="scss">
  .qrContainer {
    display: flex;
    justify-content: center;
    margin: 1.5rem 0;
  }

  .qrWrapper {
    background: #ffffff;
    color: #000000;
    padding: 1rem;
    border-radius: 0.5rem;
    display: inline-flex;
  }

  .secretLine {
    text-align: center;
  }

  .secretCode {
    user-select: all;
    font-family: monospace;
    font-size: 1.1em;
    letter-spacing: 0.08em;
    padding: 0.2em 0.4em;
    background: var(--layout-mark-bg-inactive);
    border-radius: 0.25rem;
  }
</style>
